import type { DayMode } from './day';
import type { MapSpot } from './map';
import { SPOT_STATUSES, type SpotStatus } from './spot-status';

export type LiveStats = {
  readonly mode: 'live';
  readonly total: number;
  readonly counts: Readonly<Record<SpotStatus, number>>;
  readonly occupancyPct: number;
};

export type FutureStats = {
  readonly mode: 'future';
  readonly total: number;
  readonly free: number;
  readonly reserved: number;
  readonly occupancyPct: number;
};

export type PastStats = {
  readonly mode: 'past';
  readonly total: number;
  readonly occupations: number;
  readonly reservations: number;
};

export type MapStats = LiveStats | FutureStats | PastStats;

function percent(part: number, total: number): number {
  return total === 0 ? 0 : Math.round((part / total) * 100);
}

// The legend row is the statistics row; §17 forbids a second one anywhere.
export function mapStats(spots: readonly MapSpot[], mode: DayMode): MapStats {
  const total = spots.length;
  if (mode === 'past') {
    return {
      mode,
      total,
      occupations: spots.reduce((sum, spot) => sum + spot.occupations, 0),
      reservations: spots.reduce((sum, spot) => sum + spot.reservations.length, 0),
    };
  }
  if (mode === 'future') {
    const reserved = spots.filter((spot) => spot.reservations.length > 0).length;
    return {
      mode,
      total,
      reserved,
      free: total - reserved,
      occupancyPct: percent(reserved, total),
    };
  }
  const counts: Record<SpotStatus, number> = { free: 0, reserved: 0, occupied: 0, cleaning: 0 };
  for (const spot of spots) if (spot.status !== null) counts[spot.status] += 1;
  const used = SPOT_STATUSES.filter((status) => status !== 'free').reduce(
    (sum, status) => sum + counts[status],
    0,
  );
  return { mode, total, counts, occupancyPct: percent(used, total) };
}
